import { scaleLinear, scaleThreshold, select } from "d3";

import {
  Action,
  BallAction,
  FrameStencil,
  HandoffAction,
  MarkerBall,
  MarkerPlayer,
  MarkerType,
  Point,
  StencilType,
} from "../../types";
import { getActionPath } from "../get-action-path";
import { getActionsFromMovements, getBallActions, getHandoffActions, getPlayerMarkers } from "./frame-stencil-filters";

export type ScaleFunction = (time: number) => Point;

/** ================================
 * Get Movement Scale
 ================================ */
export interface GetActionMovementScaleProps {
  action: Action;
  startPos?: Point;
}

const getActionMovementScale = ({ action, startPos }: GetActionMovementScaleProps) => {
  const { startTime, endTime, pos, midPoints, endPos } = action;
  const timeDomain = startPos ? [startTime, startTime + 1, endTime - 1, endTime] : [startTime, endTime];

  const xRange = startPos ? [startPos.x, pos.x, endPos.x, endPos.x] : [pos.x, endPos.x];
  const yRange = startPos ? [startPos.y, pos.y, endPos.y, endPos.y] : [pos.y, endPos.y];

  if (midPoints.length === 0) {
    const xScale = scaleLinear().domain(timeDomain).range(xRange).clamp(true);

    const yScale = scaleLinear().domain(timeDomain).range(yRange).clamp(true);

    const scale = (time: number) => ({ x: xScale(time), y: yScale(time) });

    return { scale, endTime };
  } else {
    const timeScale = scaleLinear().domain([startTime, endTime]).range([0, 1]).clamp(true);

    const { midPoints } = action;

    const pathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
    const path = select(pathEl);
    const pathData = getActionPath({ pos, midPoints, endPos });
    path.attr("d", pathData);

    const totalLength = path.node()?.getTotalLength() || 0;
    path.remove();

    return {
      scale: (time: number) => {
        if (time <= startTime) return startPos || pos;
        if (time >= endTime) return endPos;
        const length = timeScale(time);
        const point = path.node()?.getPointAtLength(totalLength * length);
        return { x: point?.x || pos.x, y: point?.y || pos.y };
      },
      endTime,
    };
  }
};

type MovementScale = ReturnType<typeof getActionMovementScale>;

/** ================================
 * Create Movement Scale
 ================================ */
interface CreateMovementScaleProps {
  marker: MarkerPlayer | MarkerBall;
  stencils: FrameStencil[];
}

interface MarkerPlayerWithStartTime extends MarkerPlayer {
  startTime: number;
}

export const createMovementScale = ({ marker, stencils }: CreateMovementScaleProps) => {
  const movementScales: MovementScale[] = ((): MovementScale[] => {
    switch (marker.markerType) {
      case MarkerType.PLAYER:
        return getActionsFromMovements({ movements: marker.movements, stencils }).map((action, i) =>
          getActionMovementScale({ action, ...(i === 0 ? { startPos: marker.pos } : {}) })
        );

      case MarkerType.BALL: {
        const playersAndPasses = [
          ...getPlayerMarkers(stencils),
          ...getBallActions(stencils),
          ...getHandoffActions(stencils),
        ];
        const movementStencils: (BallAction | HandoffAction | MarkerPlayerWithStartTime)[] = marker.movements
          .map((m) => playersAndPasses.find((s) => s.id === m.id))
          .filter((s): s is BallAction | HandoffAction | MarkerPlayerWithStartTime => !!s);

        return movementStencils.map((movementStencil, i, movementStencilArr): MovementScale => {
          const nextMovement = movementStencilArr[i + 1];

          switch (movementStencil.stencilType) {
            case StencilType.ACTION:
              return getActionMovementScale({ action: movementStencil, ...(i === 0 ? { startPos: marker.pos } : {}) });

            case StencilType.MARKER: {
              const nextStartTime: number = nextMovement?.startTime !== undefined ? nextMovement?.startTime : Infinity;
              const playerScale = createMovementScale({ marker: movementStencil, stencils });
              return { scale: playerScale, endTime: nextStartTime };
            }
          }
        });
      }
    }
  })();

  const scaleDomains = movementScales.map((scale) => scale.endTime);
  const scaleRanges = movementScales.map(({ scale }) => scale);

  const thresholdScaleOfScales = scaleThreshold<number, ScaleFunction>().domain(scaleDomains).range(scaleRanges);

  return (time: number) => {
    const movementScale = thresholdScaleOfScales(time);
    return movementScale ? movementScale(time) : marker.pos;
  };
};

export type MovementTimeScale = ReturnType<typeof createMovementScale>;
